import { AdditionalCostType, InconTermType } from 'app/core/incon-term/incon-term.type';

export type TransportMode = 'maritimo' | 'ar' | 'terra';
export type CostType = 'frete' | 'seguro' | 'desalfandegamento' | 'transporte' | 'outro';
export type { AdditionalCostType };

export const COST_TYPE_TO_BACKEND: Record<CostType, AdditionalCostType> = {
  frete: 'FRETE',
  seguro: 'SEGURO',
  desalfandegamento: 'DESALFANDEGAMENTO',
  transporte: 'TRANSPORTE',
  outro: 'OUTRO',
};

export const COST_TYPE_FROM_BACKEND: Record<AdditionalCostType, CostType> = {
  FRETE: 'frete',
  SEGURO: 'seguro',
  DESALFANDEGAMENTO: 'desalfandegamento',
  TRANSPORTE: 'transporte',
  OUTRO: 'outro',
};

// Matches the backend's Freight.via (prisma/schema.prisma FreightViaEnum) — not a frontend invention.
export type FreightViaEnum = 'SEA' | 'AIR' | 'LAND';

export const TRANSPORT_MODE_FROM_VIA: Record<FreightViaEnum, TransportMode> = {
  SEA: 'maritimo',
  AIR: 'ar',
  LAND: 'terra',
};

export const TRANSPORT_MODE_TO_VIA: Record<TransportMode, FreightViaEnum> = {
  maritimo: 'SEA',
  ar: 'AIR',
  terra: 'LAND',
};

export type ProposalStatus = 'draft' | 'sent' | 'accepted';

/**
 * A cost of type 'frete' reuses the existing Freight entity (via freightId on the
 * backend AdditionalCost row) instead of duplicating carrier/via/port fields —
 * design-spec decision §0.3. This shape mirrors what GET_PROPOSALS_BY_ID's
 * additionalCosts { freight { ... } } selection returns.
 */
export interface AdditionalCostFreight {
  id: number;
  company?: { id: number; name: string } | null;
  originPort?: { id: number; name: string } | null;
  destinyPort?: {
    id: number;
    name: string;
    country?: { id: number; name: string } | null;
  } | null;
  via?: FreightViaEnum | null;
  tt?: number | null; // transit time, in days
}

export interface AdditionalCost {
  id: number;
  type: CostType;
  description: string;
  value: number;
  supplierReference?: string | null;
  internalNote?: string | null;
  freight?: AdditionalCostFreight | null; // present only when type === 'frete'
}

export interface OfferSpecs {
  dryness: string;
  sawnGrade: string;
  thickness: string;
  length: string;
  quality: string;
  quantity: string;
  originPort: string;
}

export interface Offer {
  id: number; // ProductOffer2Proposal.id — the join-row id, used for addAdditionalCostToOffer
  reference: string; // ProductOffer.number, e.g. OF1754046776
  productName: string;
  supplierName: string;
  basePrice: number;
  marginPercent: number;
  specs: OfferSpecs;
  additionalCosts: AdditionalCost[];
}

// Incoterm is the real lookup table InconTerm (not a closed union) — PRD §13.1.
export type DeliveryTerms = Pick<InconTermType, 'id' | 'name' | 'includedCostTypes' | 'roundToMultiple'>;

export interface SaleConditions {
  deliveryTerms: DeliveryTerms | null;
  deliveryLocation: string; // display-only detail, e.g. "Leixões, Portugal" — never required by canSendProposal
  shippingWindow: string;
  paymentTerms: string;
  offerValidity: string;
}

export interface ProposalRecipients {
  mode: 'client' | 'clients' | 'country';
  value: string[];
}

export interface Proposal {
  id: number;
  reference: string;
  status: ProposalStatus;
  currency: 'EUR' | 'USD';
  recipients: ProposalRecipients;
  saleConditions: SaleConditions;
  offers: Offer[];
  internalNotes: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}
